import React, { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FieldSet, FormHelp, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';
import { debounce } from '@zg-rentals/zdash';

import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import TimeConstants from '../../../../constants/TimeConstants';

import { useForm } from '../../../../hooks';

import { postingConstraints } from '../../../../utils/formValidation';
import PropertiesActions from '../../../../actions/PropertiesActions';
import PostingActions from '../../../../actions/PostingActions';
import actions from '../../../../containers/CreateListingPage/actions';
import wizardActions from '../../../Wizard/actions';

import WizardAlert from '../../../WizardAlert';

const propTypes = {};
const defaultProps = {};

const radioControlName = 'leaseDuration';

const LeaseDurationFieldSet = () => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { listingAlias, leaseDuration } = useSelector((state) => state.properties.currentProperty);

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      leaseDuration,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      // eslint-disable-next-line no-use-before-define
      save({
        target: {
          name: radioControlName,
          value: inputs.leaseDuration,
        },
      });
    }
  }, [isSaveComplete]);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event) => {
    const { value, name: fieldName } = event.target;

    // field error check needs to be above must click on cta check
    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });

      return;
    }

    actions.updateHasFieldError({ reducerDispatch, hasFieldError: false });

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const dataToSave = {
      fieldName,
      value,
      propertyId: listingAlias,
    };

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PostingActions.property.edit(dataToSave))
      .then(() => {
        dispatch(PropertiesActions.updateCurrentProperty({ [fieldName]: value }));

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  const handleChange = (event) => {
    event.persist();
    clearServerError();
    handleFormChange(event);
    saveDebounced(event);
  };

  return (
    <div data-test="LeaseDurationFieldSet">
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FieldSet
        required={true}
        legend={<Legend>Lease duration</Legend>}
        description={<FormHelp error={!!errorObj.leaseDuration}>{errorObj.leaseDuration}</FormHelp>}
      >
        <LabeledControl
          controlId="1mo"
          label={<Label>1 month</Label>}
          control={
            <Radio
              name={radioControlName}
              value="1mo"
              checked={inputs.leaseDuration === '1mo'}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          controlId="6mo"
          label={<Label>6 months</Label>}
          control={
            <Radio
              name={radioControlName}
              value="6mo"
              checked={inputs.leaseDuration === '6mo'}
              onChange={handleChange}
            />
          }
        />
        <LabeledControl
          controlId="1y"
          label={<Label>1 year</Label>}
          control={
            <Radio name={radioControlName} value="1y" checked={inputs.leaseDuration === '1y'} onChange={handleChange} />
          }
        />
        <LabeledControl
          controlId="r2o"
          label={<Label>Rent to own</Label>}
          control={
            <Radio
              name={radioControlName}
              value="r2o"
              checked={inputs.leaseDuration === 'r2o'}
              onChange={handleChange}
            />
          }
        />

        <LabeledControl
          controlId="sub"
          label={<Label>Sublet/temporary</Label>}
          control={
            <Radio
              name={radioControlName}
              value="sub"
              checked={inputs.leaseDuration === 'sub'}
              onChange={handleChange}
            />
          }
        />
      </FieldSet>
    </div>
  );
};

LeaseDurationFieldSet.propTypes = propTypes;
LeaseDurationFieldSet.defaultProps = defaultProps;

export default LeaseDurationFieldSet;
